import React, { useState } from 'react';
import { ProviderMatchProviderV9 } from '@kyruus/types';
import { FormattedMessage } from '@kyruus/intl';
import _round from 'lodash/round';
import { useMachine } from '@xstate/react';

import ExpandableCardContainer from '../ExpandableCardContainer/expandable-card-container';
import ReviewsSectionHeader from './ReviewsSectionHeader';
import {
  RatingsSection,
  ReviewsDisclaimer,
  ReviewsSearchInputTitle
} from './styles';
import AverageRating from './average-rating';
import SubRatings from './sub-ratings';
import IndividualReviews from './IndividualReviews/individual-reviews';
import { NoReviewsContainer } from './IndividualReviews/styles';

import createSearchReviewsMachine, {
  STATES as SearchMachineStates,
  EVENTS as SearchMachineEvents
} from '../../lib/machines/search-reviews-machine';
import SearchInput from './search-input';

import { TrackingFunction } from '../../lib/tracking-utils';
import messages from '../../lib/messages';
import { withErrorBoundary } from '../ErrorFallback/with-error-boundary';
import { SEARCH_INPUT_REVIEW_COUNT } from '../../lib/constants';
import { AnyEventObject } from 'xstate';

interface ProviderReviewsProps {
  provider: ProviderMatchProviderV9;
  truncateAt?: number;
  track: TrackingFunction;
}

const ProviderReviews = ({
  provider,
  track
}: ProviderReviewsProps): React.ReactElement => {
  const aggregateRatings = provider.reviews?.aggregate_ratings;
  const reviewsData = provider.reviews?.reviews;
  const reviewCount = aggregateRatings?.review_count;
  const ratingCount = aggregateRatings?.rating_count;

  // Create the machine once per mount, as @xstate/react v1 did; v6 restarts the
  // actor whenever the machine passed to useMachine changes.
  const [searchMachine] = useState(() =>
    createSearchReviewsMachine({ reviews: reviewsData })
  );
  const [searchMachineState, sendToSearchMachine] = useMachine(searchMachine);

  const noResults = searchMachineState.matches(SearchMachineStates.noResults);
  // searching overrides the expanded state of the reviews UI
  // if there is no valid search, render the expanded or unexpanded reviews
  const { displayReviews } = searchMachineState.context;
  // Get the providers rating rounded to 1 precision point if available.
  const averageRating =
    aggregateRatings?.average_rating &&
    _round(aggregateRatings.average_rating, 1);
  const subRatings = aggregateRatings?.sub_ratings;
  const showSubRatings = subRatings && subRatings.length > 0;

  return (
    <ExpandableCardContainer id="profile-reviews-section" track={track}>
      <div
        itemProp="aggregateRating"
        itemScope={true}
        itemType="https://schema.org/AggregateRating"
      >
        <ReviewsSectionHeader />

        {averageRating && (
          <RatingsSection>
            <AverageRating
              averageRating={averageRating}
              ratingCount={ratingCount as number}
              reviewCount={reviewCount as number}
            />
            <ReviewsDisclaimer>
              <FormattedMessage {...messages.ratingsDisclaimer} />
            </ReviewsDisclaimer>
            {showSubRatings && <SubRatings subRatings={subRatings} />}
          </RatingsSection>
        )}

        {/*Hide search reviews input when count is less than 6*/}
        {reviewsData && reviewsData.length >= SEARCH_INPUT_REVIEW_COUNT ? (
          <>
            <ReviewsSearchInputTitle>
              <FormattedMessage {...messages.reviewsSearchInputTitle} />
            </ReviewsSearchInputTitle>
            <SearchInput
              value={searchMachineState.context.searchTerm}
              showClearIcon={true}
              onChange={(e) =>
                sendToSearchMachine({
                  type: SearchMachineEvents.searchTermUpdated,
                  data: e.currentTarget.value
                } as AnyEventObject)
              }
              onClearIconClick={() =>
                sendToSearchMachine({
                  type: SearchMachineEvents.searchTermUpdated,
                  data: ''
                } as AnyEventObject)
              }
            />
          </>
        ) : null}
      </div>

      {reviewsData && reviewsData.length > 0 ? (
        <IndividualReviews
          reviews={displayReviews}
          searchTerm={searchMachineState.context.searchTerm}
          noResults={noResults}
          onClearButtonClick={() =>
            sendToSearchMachine({
              type: SearchMachineEvents.searchTermUpdated,
              data: ''
            } as AnyEventObject)
          }
        />
      ) : (
        <NoReviewsContainer>
          <FormattedMessage {...messages.noReviews} />
        </NoReviewsContainer>
      )}
    </ExpandableCardContainer>
  );
};

export default withErrorBoundary(ProviderReviews);
